/**
 * Presentation of the engine's generated names in the interface language. The engine words them
 * in English (they are data: saved in replays, shared in multiplayer); English prints them as
 * they are, German re-sets them with grammar/de.ts. See docs/Glossary.md.
 */
import * as de from './grammar/de';
import type { Locale } from './locale';

/** What the HUD knows of a nation. */
export interface NationRef {
  name: string;
  adjective: string;
}

const ABBREV: [RegExp, string][] = [
  [/\bInfantry\b/gi, 'Inf'],
  [/\bMotori[sz]ed\b/gi, 'Mot'],
  [/\bArmou?red\b/gi, 'Armd'],
  [/\bArmou?r\b/gi, 'Armd'],
  [/\bPanzer\b/gi, 'Pz'],
  [/\bArtillery\b/gi, 'Arty'],
  [/\bReconnaissance\b/gi, 'Recon'],
  [/\bDivision\b/gi, 'Div'],
  [/\bRegiment\b/gi, 'Regt'],
  [/\bBattalion\b/gi, 'Bn'],
  [/\bBrigade\b/gi, 'Bde'],
  [/\bHeadquarters\b/gi, 'HQ'],
];

/** "12th Infantry Division" → "12th Inf Div" (the teletype prints it upper-case). */
export function shortDesignationEn(name: string): string {
  let s = name;
  for (const [re, abbr] of ABBREV) s = s.replace(re, abbr);
  return s;
}

/** A unit's full designation: "12th Infantry Division" / "12. Infanteriedivision". */
export const unitName = (en: string, locale: Locale): string => (locale === 'de' ? de.designation(en) : en);

/** A unit's staff abbreviation: "12th Inf Div" / "12. Inf.Div.". */
export const unitShort = (en: string, locale: Locale): string =>
  locale === 'de' ? de.designationShort(en) : shortDesignationEn(en);

/** A nation's full name: "Kingdom of Varnia" / "Königreich Varnia". */
export const nationName = (n: NationRef, locale: Locale): string =>
  locale === 'de' ? de.nation(n).bare : n.name;

/** A nation in a label or chip: "Varnian" / "Varnia". */
export const nationLabel = (n: NationRef, locale: Locale): string =>
  locale === 'de' ? de.nationShort(n) : n.adjective;

/** A town: generated names never change; real towns take their German name ("Warschau"). */
export const cityName = (en: string, locale: Locale): string => (locale === 'de' ? de.town(en) : en);

/** A province: "Zlatovina Heights" / "Zlatovina-Höhen". */
export const regionName = (en: string, locale: Locale): string => (locale === 'de' ? de.region(en).name : en);

/** The theatre as a title: "The Varnian Marches" / "Die Varnischen Marken". */
export const theatreTitle = (en: string, locale: Locale): string =>
  locale === 'de' ? de.theatre(en).title : en;

// ---- places --------------------------------------------------------------------------------------

/**
 * Where something happened, as dispatches name it: the city on the hex, the nearest city, the
 * province, a bare map square, or nowhere in particular. Resolved once (game/format.ts
 * `placeOf`) and worded per language.
 */
export type Place = { kind: 'city' | 'near' | 'region' | 'hex'; name: string } | { kind: 'front' };

/** The place on its own: "Dornheim", "Zlatovina Heights" / "Zlatovina-Höhen", "Planquadrat 12·4". */
export function placeLabel(p: Place, locale: Locale): string {
  if (p.kind === 'front') return locale === 'de' ? 'die Front' : 'the front';
  if (locale === 'de') {
    if (p.kind === 'region') return de.region(p.name).name;
    if (p.kind === 'hex') return `Planquadrat ${p.name}`;
    return de.town(p.name);
  }
  return p.name;
}

/** With its preposition: "at Dornheim", "near Dornheim" / "in Dornheim", "bei Dornheim", "im X-Wald". */
export function placePhraseOf(p: Place, locale: Locale): string {
  if (locale === 'de') {
    switch (p.kind) {
      case 'front':
        return 'an der Front';
      case 'city':
        return `in ${de.town(p.name)}`;
      case 'near':
        return `bei ${de.town(p.name)}`;
      case 'region':
        return de.region(p.name).inside;
      case 'hex':
        return `im Planquadrat ${p.name}`;
    }
  }
  switch (p.kind) {
    case 'front':
      return 'at the front';
    case 'city':
    case 'hex':
      return `at ${p.name}`;
    case 'near':
      return `near ${p.name}`;
    case 'region':
      return `in ${p.name}`;
  }
}

/** German dative for "über …", "von …": "der Zlatovina", "dem Dornmark-Wald", "Dornheim". */
export function placeDativeDe(p: Place): string {
  if (p.kind === 'region') return de.region(p.name).dat;
  if (p.kind === 'front') return 'der Front';
  return placeLabel(p, 'de');
}
